/**
 * @fileoverview Shared read-only TipTap renderer for inspection header form content.
 * @module ui-admin/template-builder
 *
 * Responsibilities:
 * - Render the rich-text HTML content of a header form in a read-only TipTap editor.
 * - Support a configurable empty-state placeholder message so the same component can
 *   be reused in both the template canvas preview context and the live inspection
 *   conductor context with appropriate messaging.
 *
 * Dependencies:
 * - @tiptap/react (useEditor, EditorContent) + extension suite (StarterKit, Underline,
 *   TextStyle, Color, Highlight, TextAlign, Image, FontFamily)
 * - lucide-react (FileText icon for empty state)
 *
 * Architecture:
 * - Pure presentation component; no data fetching, Dexie reads, or API calls.
 * - Shared between tenant-web (TemplatePreviewMode, InspectionConductorModal) and
 *   system-web (SystemTemplatePreviewMode) to ensure header form content is rendered
 *   identically on all surfaces.
 *
 * Notes:
 * - Uses the same TipTap extension set as the authoring editor (HeaderRichEditor) so
 *   serialised HTML is rendered faithfully without sanitisation gaps that a DOMPurify
 *   path would introduce.
 * - StarterKit is configured with underline: false so the standalone Underline extension
 *   can be registered without producing a duplicate-extension warning.
 * - The editor instance is created once on mount. Content sync on prop change is handled
 *   via setContent rather than remounting, preserving scroll position.
 */

import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { FontFamily } from '@tiptap/extension-font-family';
import { FileText } from 'lucide-react';

/**
 * @component HeaderFormRenderer
 *
 * Purpose:
 * - Displays the rich-text HTML content of an inspection header form in read-only mode.
 *   Shared between the template canvas preview and the live inspection conductor so
 *   header content is rendered identically on all surfaces.
 *
 * Props:
 * - html: TipTap-serialised HTML string stored in form.description. Undefined or an
 *   empty/blank paragraph triggers the empty-state placeholder.
 * - emptyText: Optional override for the placeholder copy shown when html is absent or
 *   empty. Defaults to canvas-oriented copy; pass an inspection-context message when
 *   used inside the conductor.
 *
 * Behavior:
 * - Initialises a TipTap editor with editable=false so the HTML is processed through
 *   the same extension schema used during authoring — marks, colours, and alignment
 *   that would be stripped by a sanitiser are preserved.
 * - When the html prop changes (e.g. the user edits the template and returns to preview)
 *   the content is synced via setContent without remounting the editor.
 *
 * Data Source:
 * - local — receives html from the parent's form record; no Dexie reads.
 *
 * Constraints:
 * - Read-only; editable is always false. Must never expose an edit toolbar.
 *
 * Performance:
 * - Editor is created once per mount. setContent is O(1) and does not re-parse
 *   the full extension schema on each prop change.
 */
export function HeaderFormRenderer({
  html,
  emptyText = 'No content yet — edit the header form on the canvas to add instructions or rich text.',
}: {
  html: string | undefined;
  emptyText?: string;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ underline: false }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      FontFamily,
    ],
    content: html || '<p></p>',
    editable: false,
  });

  // Sync content when the html prop changes (e.g. user edits and comes back to preview)
  useEffect(() => {
    if (editor && html !== undefined && html !== editor.getHTML()) {
      editor.commands.setContent(html || '<p></p>');
    }
  }, [html, editor]);

  if (!html || html === '<p></p>' || html === '') {
    return (
      <div className="px-6 py-8 flex flex-col items-center gap-2 text-center">
        <FileText size={28} className="text-gray-300" />
        <p className="text-sm text-gray-400 italic">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      <div className="prose prose-sm max-w-none text-gray-800">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
